import classNames from "classnames";
import { CategorySelector } from "components/CategorySelector/CategorySelector";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { TableUI } from "components/design/TableUI";
import { TableEmptyState } from "components/TableEmptyState/TableEmptyState";
import ToolTip from "components/design/toolTip";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Search } from "components/DesignSystem/Search/Search";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { FileInput, FileType } from "components/FileInput/FileInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { ApplyToAllIcon } from "components/icons/ApplyToAllIcon";
import { BankLogo } from "components/icons/BankLogo";
import { EmptyHourGlass } from "components/icons/EmptyHourGlass";
import { Preview } from "components/PreviewModal";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { TRANSACTION_SOURCE } from "dictionaries";
import { useFormikContext } from "formik";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import qs from "qs";
import { useState, useMemo, useEffect, useRef, useCallback, memo } from "react";
import { debounce } from "utils/debouncing";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  createColumnHelper,
  getCoreRowModel,
  SortingState,
  useReactTable,
} from "react-table-8.10.7";
import { actionItemsApi } from "store/apis/actionItems";
import { useGetGroupDataQuery } from "store/apis/group";
import {
  OpenItemFilterParams,
  TransactionWithOpenItemInfo,
  useBulkAddClarificationMutation,
  useDeleteOpenItemInvoiceMutation,
  useGetOpenItemTransactionsQuery,
  useUploadOpenItemInvoiceMutation,
} from "store/apis/openItem";
import OpenItemsFilter from "./OpenItemsFilter";
import { useUpdateTransactionMutation } from "store/apis/transactions";
import {
  openFloatingChat,
  setMessageIdForTransactionList,
  setPopulateTransactionIDs,
  setToOpenChatId,
} from "store/slices/chat";
import { closeAddRequestInfoModal } from "store/slices/openItem";
import { resetOpenItemFilter } from "store/slices/openItemFilter";
import { RootState } from "store/store";
import { FileObject } from "types/Models/fileObject";
import {
  ItemStatus,
  OPEN_ITEM_STATUS,
  OpenItem,
  OpenItemStatus,
} from "types/Models/openItem";
import { BackendError } from "types/utils/error";
import { pluralize } from "utils/pluralize";
import { TxnAmount } from "./TransactionColumn";
import { DocumentTagMultiSelect } from "components/DocumentTags/DocumentTagMultiSelect";
import { useConfirmDocumentTagsMutation } from "store/apis/documentTagReview";
import { DocumentTag } from "types/Models/documentTagReview";

type FormValues = {
  openItems: {
    uuid: string;
    clarification: string;
    invoice: FileObject | null;
    status: ItemStatus;
  }[];
};

type AddRequestInfoRow = TransactionWithOpenItemInfo & {
  openItem: OpenItem;
};

type SetFieldValue = (field: string, value: any) => void;

type TableMetaType = {
  status: OpenItemStatus | undefined;
  setFieldValue: SetFieldValue;
  textareaSetters: { current: Map<string, (text: string) => void> };
  draftClarifications: { current: Map<string, string> };
};

const columnHelper = createColumnHelper<AddRequestInfoRow>();

const AddInformation = ({
  openItem,
  index,
  status,
  setFieldValue,
  textareaSetters,
  draftClarifications,
}: {
  openItem: OpenItem;
  index: number;
  status: OpenItemStatus | undefined;
  setFieldValue: SetFieldValue;
  textareaSetters: { current: Map<string, (text: string) => void> };
  draftClarifications: { current: Map<string, string> };
}) => {
  const [focusedItem, setFocusedItem] = useState<boolean>(false);
  const { clarification, uuid } = openItem;
  const [localValue, setLocalValue] = useState(
    draftClarifications.current.get(uuid) ?? clarification ?? ""
  );

  const isReadOnly = status !== OPEN_ITEM_STATUS.REQUESTED;

  useEffect(() => {
    textareaSetters.current.set(uuid, setLocalValue);
    return () => {
      textareaSetters.current.delete(uuid);
    };
  }, [uuid, textareaSetters]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setLocalValue(value);
    setFieldValue(`openItems[${index}].clarification`, value);
    draftClarifications.current.set(uuid, value);
  };

  if (isReadOnly) {
    return (
      <div className="t-flex t-items-center t-gap-1.5">
        <ConditionalToolTip condition={clarification} propClass="t-max-w-96">
          <div className="t-line-clamp-2 t-w-60 t-text-subtext-sm t-text-text-60 t-break-words">
            {clarification || "-"}
          </div>
        </ConditionalToolTip>
        {Boolean(openItem.invoice) && clarification && (
          <div className="t-h-6 t-w-px t-bg-neutral-20 t-mx-1" />
        )}
        {Boolean(openItem.invoice) && (
          <Invoice
            openItem={openItem}
            isRemovable={false}
            index={index}
            status={status}
            setFieldValue={setFieldValue}
          />
        )}
      </div>
    );
  }

  return (
    <div className="t-flex t-items-center t-gap-1.5">
      <div className="t-min-w-0 t-flex-1">
        <TextArea
          block
          size="small"
          name={`openItems[${index}].clarification`}
          rows={1}
          id={uuid}
          value={localValue}
          onChange={handleChange}
          onFocus={() => setFocusedItem(true)}
          onBlur={() => {
            setTimeout(() => setFocusedItem(false), 300);
          }}
          placeholder="Enter information..."
        />
      </div>
      {/* Kept mounted so focusing the field does not shift the row */}
      <div
        className={classNames("t-shrink-0", { "t-invisible": !focusedItem })}
      >
        <ApplyToAll
          openItem={openItem}
          currentValue={localValue}
          textareaSetters={textareaSetters}
        />
      </div>
      <div className="t-h-6 t-w-px t-shrink-0 t-bg-neutral-20 t-mx-1" />
      <div className="t-shrink-0">
        <Invoice
          openItem={openItem}
          index={index}
          status={status}
          setFieldValue={setFieldValue}
        />
      </div>
    </div>
  );
};

const Invoice = ({
  openItem,
  isRemovable = true,
  index,
  status,
  setFieldValue,
}: {
  openItem: OpenItem;
  isRemovable?: boolean;
  index: number;
  status: OpenItemStatus | undefined;
  setFieldValue: SetFieldValue;
}) => {
  const { invoice, uuid } = openItem;
  const { successToast, alertToast } = useToast();
  const previewModal = useModal();

  const [uploadOpenItemInvoice, { isLoading: isUploading }] =
    useUploadOpenItemInvoiceMutation();

  const [deleteOpenItemInvoice, { isLoading: isDeleting }] =
    useDeleteOpenItemInvoiceMutation();

  const { groupId } = useSelector(
    (state: RootState) => state.openItem.addRequestInfoModal
  );

  const uploadInvoice = async ({
    openItemId,
    file,
  }: {
    openItemId: string;
    file: FileType;
  }) => {
    try {
      await uploadOpenItemInvoice({
        groupId,
        openItemId,
        file: file as File,
      }).unwrap();
      successToast({ message: "File Uploaded" });
      setFieldValue(`openItems.${index}.invoice`, file);
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
    }
  };

  const onInvoiceDelete = async ({ openItemId }: { openItemId: string }) => {
    try {
      await deleteOpenItemInvoice({ openItemId, groupId }).unwrap();
      successToast({ message: "File Deleted" });
      setFieldValue(`openItems.${index}.invoice`, null);
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
    }
  };

  if (status === OPEN_ITEM_STATUS.COMPLETED && !Boolean(invoice)) {
    return "-";
  }

  return (
    <>
      <FileInput
        file={invoice}
        onDrop={(files) => uploadInvoice({ openItemId: uuid, file: files[0] })}
        isUploading={isUploading}
        variant="icon"
        onFileClick={previewModal.open}
        isDeleting={isDeleting}
        onDelete={
          status !== OPEN_ITEM_STATUS.COMPLETED && isRemovable
            ? (e) => onInvoiceDelete({ openItemId: uuid })
            : undefined
        }
      />
      <Preview
        closeModal={previewModal.close}
        showModal={previewModal.isOpen}
        previewId={invoice?.uuid}
        groupId={groupId}
      />
    </>
  );
};

const ApplyToAll = ({
  openItem,
  currentValue,
  textareaSetters,
}: {
  openItem: OpenItem;
  currentValue: string;
  textareaSetters: { current: Map<string, (text: string) => void> };
}) => {
  const { setValues, values } = useFormikContext<FormValues>();

  const copyTextToAll = () => {
    setValues({
      openItems: values.openItems.map((item) => ({
        ...item,
        clarification: currentValue,
      })),
    });
    textareaSetters.current.forEach((setter) => setter(currentValue));
  };

  return (
    <ToolTip text="Apply to all">
      <Button
        type="button"
        customType="ghost_icon"
        size="small"
        onClick={copyTextToAll}
      >
        <span className="t-text-text-30">
          <ApplyToAllIcon />
        </span>
      </Button>
    </ToolTip>
  );
};

type InvoiceWithTags = { uuid: string; document_tags?: DocumentTag[] };

const DocumentTagsColumn = ({ openItem }: { openItem: OpenItem }) => {
  const { alertToast } = useToast();
  const { groupId } = useSelector(
    (state: RootState) => state.openItem.addRequestInfoModal
  );
  const [tagUuids, setTagUuids] = useState<string[] | null>(null);
  const [confirmDocumentTags] = useConfirmDocumentTagsMutation();

  const invoice = openItem.invoice as InvoiceWithTags | undefined;

  if (!invoice?.uuid) {
    return (
      <span className="t-text-body-sm-italic t-text-text-30 t-italic">
        No document uploaded
      </span>
    );
  }

  const selectedUuids =
    tagUuids ?? (invoice.document_tags || []).map((tag) => tag.uuid);

  const onTagsChange = async (newTagUuids: string[]) => {
    setTagUuids(newTagUuids);
    try {
      await confirmDocumentTags({
        groupId,
        messageId: openItem.message_id ?? undefined,
        documents: [{ document_id: invoice.uuid, tag_ids: newTagUuids }],
      }).unwrap();
    } catch (err) {
      alertToast({
        message:
          (err as BackendError)?.data?.error?.message ?? "Something went wrong",
      });
    }
  };

  return (
    <DocumentTagMultiSelect
      groupId={groupId}
      selectedUuids={selectedUuids}
      onChange={onTagsChange}
    />
  );
};

const CategoryColumn = ({ openItem }: { openItem: OpenItem }) => {
  const { alertToast, successToast } = useToast();
  const [updateTransaction, { isLoading: isUpdating, originalArgs }] =
    useUpdateTransactionMutation();

  const { groupId, entityId } = useSelector(
    (state: RootState) => state.openItem.addRequestInfoModal
  );

  const assignCategoryToTxn = async ({
    transactionCategoryId,
    previewTxnId,
  }: {
    transactionCategoryId?: string | null;
    previewTxnId: string;
  }) => {
    try {
      if (entityId) {
        const transactionResponse = await updateTransaction({
          groupId,
          entityId,
          previewTxnId,
          payload: {
            transaction_category_uuid: transactionCategoryId || null,
          },
        }).unwrap();

        if (transactionResponse?.transaction?.category) {
          successToast({ message: "Category assigned" });
        } else {
          successToast({ message: "Category removed" });
        }
      }
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return (
    <CategorySelector
      hiddenCategoryTypes={[]}
      name={`transactions[${openItem.transaction_id}].category`}
      category={openItem.transaction_category?.uuid || null}
      size="regular"
      menuPortalStyle={{
        width: 450,
        marginLeft: -312,
      }}
      isUpdating={
        isUpdating && originalArgs?.previewTxnId === openItem.transaction_id
      }
      onChange={(option) =>
        assignCategoryToTxn({
          transactionCategoryId:
            option instanceof Array ? option[0].value : option?.value,
          previewTxnId: openItem.transaction_id || "",
        })
      }
    />
  );
};

const columns = [
  columnHelper.accessor((row) => row.transaction, {
    id: "Date",
    enableSorting: true,
    cell: (info) => {
      const { date } = info.getValue();
      return (
        <div className="t-text-subtext-sm t-text-text-30 t-whitespace-nowrap">
          {dayjs(date).format(DISPLAY_DATE)}
        </div>
      );
    },
    header: "Date",
    size: 8,
  }),
  columnHelper.accessor((row) => row.transaction, {
    id: "vendor",
    enableSorting: true,
    cell: (info) => {
      const { merchant, logo } = info.getValue();
      if (!merchant) return <span className="t-text-text-30">-</span>;
      return (
        <div className="t-flex t-items-center t-gap-2">
          <Avatar src={logo || ""} alt={merchant} />
          <div className="t-text-subtext-sm t-text-text-60 t-break-words">
            {merchant}
          </div>
        </div>
      );
    },
    header: "Vendor",
    size: 10,
  }),
  columnHelper.accessor((row) => row.transaction.from, {
    id: "source",
    cell: (info) => {
      const from = info.getValue();
      if (!from) return <span className="t-text-text-30">-</span>;

      const { bank_account } = from || {};
      const { bank_brand } = bank_account || {};
      const { logo_url } = bank_brand || {};
      const mask = bank_account?.mask ? `•••• ${bank_account.mask}` : "";

      return (
        <ConditionalToolTip
          condition={
            from &&
            `${from?.bank_account?.nickname} ${mask} (${
              TRANSACTION_SOURCE[from?.source!]
            })`
          }
        >
          <div className="t-flex t-items-center t-justify-center">
            {logo_url ? (
              <Avatar src={logo_url} alt={from?.bank_account?.mask || "Bank"} />
            ) : (
              <BankLogo />
            )}
          </div>
        </ConditionalToolTip>
      );
    },
    header: () => <div className="t-text-center">Source</div>,
    enableSorting: false,
    size: 5,
  }),
  columnHelper.accessor((row) => row.transaction.description, {
    id: "description",
    enableSorting: true,
    cell: (info) => {
      const description = info.getValue();
      return (
        <ConditionalToolTip condition={description} propClass="t-max-w-96">
          <div className="t-max-w-72 t-truncate t-text-subtext-sm t-text-text-60">
            {description || "-"}
          </div>
        </ConditionalToolTip>
      );
    },
    header: "Description",
    size: 14,
  }),
  columnHelper.accessor((row) => row.transaction, {
    id: "Amount",
    enableSorting: true,
    cell: (info) => {
      const { amount = 0 } = info.getValue();
      return <TxnAmount amount={Number(amount)} />;
    },
    header: () => <div className="t-flex t-justify-end">Amount</div>,
    size: 8,
  }),
  columnHelper.accessor((row) => row.transaction.information_request, {
    id: "Information Requested",
    enableSorting: false,
    cell: (info) => {
      const information_request = info.getValue();
      return (
        <ConditionalToolTip
          condition={information_request}
          propClass="t-max-w-96"
        >
          <div className="t-max-w-72 t-truncate t-text-subtext-sm t-text-text-60">
            {information_request || "-"}
          </div>
        </ConditionalToolTip>
      );
    },
    header: "Info Requested",
    size: 12,
  }),
  columnHelper.display({
    id: "Add Information",
    cell: (info) => {
      const { status, setFieldValue, textareaSetters, draftClarifications } =
        info.table.options?.meta as TableMetaType;
      return (
        <AddInformation
          openItem={info.row.original.openItem}
          index={info.row.index}
          status={status}
          setFieldValue={setFieldValue}
          textareaSetters={textareaSetters}
          draftClarifications={draftClarifications}
        />
      );
    },
    header: (info) => {
      const { status } = info.table.options?.meta as TableMetaType;
      return (
        <Label required={status !== OPEN_ITEM_STATUS.COMPLETED}>
          Add Info/Upload Invoice
        </Label>
      );
    },
    size: 20,
  }),
  columnHelper.display({
    id: "category",
    size: 15,
    header: "Category",
    cell: (info) => <CategoryColumn openItem={info.row.original.openItem} />,
  }),
  columnHelper.display({
    id: "documentTags",
    size: 22,
    header: "Document Tags",
    cell: (info) => (
      <DocumentTagsColumn openItem={info.row.original.openItem} />
    ),
  }),
];

const ValidationWrapper = ({
  children,
}: {
  children: ({
    isValid,
    newlyAddedClarifications,
  }: {
    isValid: boolean;
    newlyAddedClarifications: FormValues["openItems"];
  }) => React.ReactNode;
}) => {
  const { values } = useFormikContext<FormValues>();
  const { openItems } = values;

  const newlyAddedClarifications = openItems.filter(
    (item) => item.clarification.trim().length > 0 || Boolean(item.invoice)
  );

  const isValid = newlyAddedClarifications.length > 0;

  return children({
    isValid,
    newlyAddedClarifications,
  });
};

type StableModalContentProps = {
  status: OpenItemStatus | undefined;
  rowsLength: number;
  searchInput: string;
  setSearchInput: (value: string) => void;
  debouncedSetSearch: (value: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  table: any;
  isTxnFetching: boolean;
  showFullLoader: boolean;
};

const StableModalContent = memo(
  ({
    status,
    rowsLength,
    searchInput,
    setSearchInput,
    debouncedSetSearch,
    table,
    isTxnFetching,
    showFullLoader,
  }: StableModalContentProps) => {
    if (showFullLoader) {
      return (
        <>
          <Modal.Header>
            <Skeleton className="t-h-6 t-w-48" />
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="t-flex t-flex-col t-gap-2">
            <Skeleton className="t-h-10" />
            <Skeleton className="t-h-10" />
            <Skeleton className="t-h-10" />
            <Skeleton className="t-h-10" />
            <Skeleton className="t-h-10" />
            <Skeleton className="t-h-10" />
          </Modal.Body>
        </>
      );
    }

    return (
      <>
        <Modal.Header>
          <div className="t-flex t-items-center t-gap-2">
            <Modal.Title>
              {status === "REQUESTED" ? "Add Info" : "View Info"}
            </Modal.Title>
            <Tag tagType="dark_gray" size="small" icon={false} rounded>
              {pluralize(rowsLength, "item", "items")}
            </Tag>
          </div>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body
          className="t-flex t-flex-col t-gap-3 t-flex-1 t-min-h-0"
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
              e.preventDefault();
            }
          }}
        >
          <div className="t-flex t-items-center t-gap-2">
            <Search
              placeholder="Search"
              value={searchInput}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSearchInput(e.target.value);
                debouncedSetSearch(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
              className="t-w-72"
            />
            <OpenItemsFilter />
          </div>
          {!isTxnFetching && rowsLength === 0 ? (
            <div className="t-flex t-flex-1 t-items-center t-justify-center">
              <TableEmptyState
                icon={<EmptyHourGlass />}
                title="No transactions found"
                description={
                  searchInput
                    ? "Try adjusting your search or filters"
                    : "No open items to display"
                }
              />
            </div>
          ) : (
            <TableUI
              table={table}
              verticalAlign="middle"
              enableSort
              isLoading={isTxnFetching}
              className="[&>table]:t-min-w-[1180px]"
            />
          )}
        </Modal.Body>
      </>
    );
  }
);

const SORT_COL_MAP: Record<string, string> = {
  Date: "DATE",
  vendor: "VENDOR",
  Amount: "AMOUNT",
};

type InfoUIProps = {
  onClose: () => void;
};

const InfoUI = ({ onClose }: InfoUIProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { alertToast, successToast } = useToast();
  const [bulkAddClarification] = useBulkAddClarificationMutation();
  const { isCustomer, isCpa, isAdmin } = useRoleBasedView();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const debouncedSetSearch = useMemo(() => debounce(setSearch), []);
  const formRef = useRef<HTMLFormElement>(null);
  const hasLoadedOnce = useRef(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const setFieldValueRef = useRef<SetFieldValue>(() => {});
  const textareaSetters = useRef<Map<string, (text: string) => void>>(
    new Map()
  );
  const draftClarifications = useRef<Map<string, string>>(new Map());
  const stableSetFieldValue = useCallback<SetFieldValue>((field, value) => {
    setFieldValueRef.current(field, value);
  }, []);

  useEffect(() => {
    const form = formRef.current;
    if (!form) return;
    const preventSubmit = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };
    form.addEventListener("submit", preventSubmit);
    return () => form.removeEventListener("submit", preventSubmit);
  }, []);

  const {
    csvId,
    groupId,
    channelId,
    entityId,
    status: reduxStatus,
  } = useSelector((state: RootState) => state.openItem.addRequestInfoModal);

  const { data: groupData } = useGetGroupDataQuery(
    { groupId },
    { skip: !groupId }
  );
  const entities = groupData?.entities || [];
  const isUSBooksChannel = entities.some(
    (entity) =>
      entity.uuid === entityId && entity.bookkeeping_channel === channelId
  );
  const canManageTags = (isCpa || isAdmin) && isUSBooksChannel;

  const status = reduxStatus || undefined;

  const openItemFilters = useSelector(
    (state: RootState) => state.openItemFilter
  );

  const sortParams = useMemo((): OpenItemFilterParams => {
    if (!sorting.length) return {};
    const [{ id, desc }] = sorting;
    const sort_col = SORT_COL_MAP[id];
    if (!sort_col) return {};
    return { sort_col, sort_order: desc ? "DSC" : "ASC" };
  }, [sorting]);

  const filterParams: OpenItemFilterParams = {
    search_term: search || undefined,
    start_date: openItemFilters.startDate || undefined,
    end_date: openItemFilters.endDate || undefined,
    min_amount: openItemFilters.minAmount || undefined,
    max_amount: openItemFilters.maxAmount || undefined,
    cash_flow: openItemFilters.cashFlow || undefined,
    merchants: openItemFilters.vendors.length
      ? openItemFilters.vendors.join(",")
      : undefined,
    category_ids: openItemFilters.categoryIds.length
      ? openItemFilters.categoryIds.join(",")
      : undefined,
    from_ids: openItemFilters.fromIds.length
      ? openItemFilters.fromIds.join(",")
      : undefined,
    journal_entry: openItemFilters.manualEntry || undefined,
    bank_statement: openItemFilters.bankStatement || undefined,
    has_no_merchants: openItemFilters.hasNoVendors || undefined,
    ...sortParams,
  };

  const {
    data: txnData,
    isLoading: isTxnLoading,
    isFetching: isTxnFetching,
  } = useGetOpenItemTransactionsQuery(
    { groupId, entityId, csvId, ...filterParams },
    {
      skip: !groupId || !entityId || !csvId,
      refetchOnMountOrArgChange: true,
    }
  );

  if (txnData) {
    hasLoadedOnce.current = true;
  }
  const showFullLoader = isTxnLoading && !hasLoadedOnce.current;

  const rows: AddRequestInfoRow[] = useMemo(
    () =>
      (txnData?.transactions || [])
        .filter((txn) => Boolean(txn.transaction.open_item))
        .map((txn) => ({ ...txn, openItem: txn.transaction.open_item! })),
    [txnData]
  );

  const initialValues = useMemo<FormValues>(
    () => ({
      openItems: rows.map(
        ({
          openItem: { uuid, clarification, invoice, status: oistatusValue },
        }) => ({
          uuid,
          clarification:
            draftClarifications.current.get(uuid) ?? clarification ?? "",
          invoice: invoice || null,
          status: oistatusValue || "REQUESTED",
        })
      ),
    }),
    [rows]
  );

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    state: {
      sorting,
      columnVisibility: {
        category: !isCustomer,
        documentTags: canManageTags,
      },
    },
    onSortingChange: setSorting,
    defaultColumn: {
      size: 10,
      minSize: 3,
      maxSize: 100,
    },
    meta: {
      status,
      setFieldValue: stableSetFieldValue,
      textareaSetters,
      draftClarifications,
    } satisfies TableMetaType,
  });

  const openItemsTxnIds = rows.map((r) => r.transaction.uuid).join(",");

  const onSubmit = async (values: FormValues) => {
    try {
      await bulkAddClarification({
        groupId,
        csv_id: csvId,
        open_items: values.openItems.map((item) => ({
          uuid: item.uuid,
          clarification: item.clarification,
        })),
      }).unwrap();

      draftClarifications.current.clear();
      dispatch(setToOpenChatId(channelId));
      dispatch(openFloatingChat());
      successToast({ message: "Submitted" });
      onClose();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  const viewTransactions = () => {
    const queryParams = qs.stringify(
      {
        company: isCustomer ? null : groupId,
        entity: entityId,
      },
      { skipNulls: true, addQueryPrefix: true }
    );

    history.push(`/books/transactions${queryParams}`);
    dispatch(setPopulateTransactionIDs(openItemsTxnIds));
    dispatch(setMessageIdForTransactionList(""));
    onClose();
  };

  return (
    <Modal.Content
      size="fullscreen"
      asChild
      onInteractOutside={(e) => e.preventDefault()}
      onEscapeKeyDown={(e) => {
        if (document.activeElement instanceof HTMLInputElement) {
          e.preventDefault();
        }
      }}
    >
      <FormikForm<FormValues>
        ref={formRef}
        initialValues={initialValues}
        onSubmit={onSubmit}
        validateOnMount
        enableReinitialize
      >
        {({ submitForm, isSubmitting, setFieldValue }) => {
          setFieldValueRef.current = setFieldValue as unknown as SetFieldValue;
          return (
            <>
              <StableModalContent
                status={status}
                rowsLength={rows.length}
                searchInput={searchInput}
                setSearchInput={setSearchInput}
                debouncedSetSearch={debouncedSetSearch}
                table={table}
                isTxnFetching={isTxnFetching}
                showFullLoader={showFullLoader}
              />
              {!showFullLoader && (
                <Modal.Footer className="t-flex t-justify-end t-gap-3 t-items-center">
                  <Button
                    customType="text"
                    type="button"
                    onClick={viewTransactions}
                  >
                    View in Transactions
                  </Button>
                  {status === OPEN_ITEM_STATUS.REQUESTED && (
                    <ValidationWrapper>
                      {({ isValid, newlyAddedClarifications }) => (
                        <Button
                          customType="primary"
                          disabled={!isValid || isSubmitting}
                          onClick={submitForm}
                          isLoading={isSubmitting}
                        >
                          Submit{" "}
                          {isValid && (
                            <>
                              (
                              {pluralize(
                                newlyAddedClarifications.length,
                                "transaction",
                                "transactions"
                              )}
                              )
                            </>
                          )}
                        </Button>
                      )}
                    </ValidationWrapper>
                  )}
                </Modal.Footer>
              )}
            </>
          );
        }}
      </FormikForm>
    </Modal.Content>
  );
};

export const AddRequestInfo = () => {
  const dispatch = useDispatch();

  const { csvId } = useSelector(
    (state: RootState) => state.openItem.addRequestInfoModal
  );

  const onClose = () => {
    dispatch(closeAddRequestInfoModal());
    dispatch(resetOpenItemFilter());
    setTimeout(() => {
      dispatch(
        actionItemsApi.util.invalidateTags([{ type: "ALL_ACTION_ITEMS" }])
      );
    }, 1000);
  };

  return (
    <Modal.Root open={Boolean(csvId)} onOpenChange={onClose} modal={false}>
      <InfoUI onClose={onClose} />
    </Modal.Root>
  );
};
